import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { useMfaEnrollment } from "@/hooks/useMfaEnrollment";
import { toast } from "sonner";
import { ShieldCheck, Loader2, LogOut } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { MfaQrCodeBody } from "./MfaQrCodeBody";

interface MfaEnrollmentScreenProps {
  onComplete: () => void;
}

/**
 * Full-screen MFA enrollment screen shown to local users who don't have MFA set up yet.
 * Blocks app access until TOTP is enrolled and verified.
 */
export function MfaEnrollmentScreen({ onComplete }: MfaEnrollmentScreenProps) {
  const { t } = useTranslation();
  const { signOut } = useAuth();

  const {
    enrollmentData,
    otpCode,
    setOtpCode,
    isSubmitting,
    isEnrolling,
    error,
    handleStartEnroll,
    handleVerify,
    handleCopySecret,
  } = useMfaEnrollment({
    onVerifySuccess: () => {
      toast.success(t("mfa.loginSuccess"), {
        description: t("mfa.welcomeBack"),
      });
      onComplete();
    },
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <ShieldCheck className="h-8 w-8 text-primary" />
          </div>
          <CardTitle>{t("mfa.enrollmentRequiredTitle")}</CardTitle>
          <CardDescription>{t("mfa.enrollmentRequiredDescription")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Step 1: Start enrollment */}
          {!enrollmentData && (
            <div className="space-y-4">
              <Alert>
                <ShieldCheck className="h-4 w-4" />
                <AlertDescription>{t("mfa.enrollmentRequiredInfo")}</AlertDescription>
              </Alert>
              <Button onClick={handleStartEnroll} disabled={isEnrolling} className="w-full">
                {isEnrolling && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("mfa.enable")}
              </Button>
            </div>
          )}

          {/* Step 2: QR Code + verify */}
          {enrollmentData && (
            <MfaQrCodeBody
              qrCode={enrollmentData.qrCode}
              secret={enrollmentData.secret}
              otpCode={otpCode}
              isSubmitting={isSubmitting}
              error={error}
              autoFocus
              onOtpChange={setOtpCode}
              onVerify={handleVerify}
              onCopySecret={handleCopySecret}
            />
          )}

          <Button variant="ghost" className="w-full" onClick={signOut}>
            <LogOut className="mr-2 h-4 w-4" />
            {t("mfa.logout")}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
